import axios from 'axios'
import { MailCheck } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { authApi } from '@/api/auth'
import { GuestAuthShell } from '@/components/auth/GuestAuthShell'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { useMinCopy } from '@/i18n/useUiLocale'
import { useAuthStore } from '@/store/authStore'

type VerificationStatus = 'waiting' | 'verifying' | 'error'

export function EmailVerificationPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const setAuth = useAuthStore((state) => state.setAuth)
  const t = useMinCopy({ preferGuest: true }).auth
  const token = searchParams.get('token')
  const stateEmail = (location.state as { email?: string } | null)?.email ?? ''
  const [email, setEmail] = useState(stateEmail)
  const [status, setStatus] = useState<VerificationStatus>(token ? 'verifying' : 'waiting')
  const [message, setMessage] = useState(() =>
    token ? t.verifyingMessage : t.verificationSent,
  )
  const [isResending, setIsResending] = useState(false)
  const verificationStarted = useRef(false)

  useEffect(() => {
    if (!token || verificationStarted.current) return
    verificationStarted.current = true

    const verify = async () => {
      try {
        const tokenResponse = await authApi.confirmEmailVerification(token)
        const profileResponse = await authApi.getProfile(tokenResponse.data.accessToken)
        setAuth(tokenResponse.data.accessToken, profileResponse.data)
        navigate('/dashboard', { replace: true })
      } catch {
        setStatus('error')
        setMessage(t.verificationLinkInvalid)
      }
    }

    void verify()
  }, [navigate, setAuth, t.verificationLinkInvalid, token])

  const resend = async () => {
    const normalizedEmail = email.trim()
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      setStatus('error')
      setMessage(t.enterRegisteredEmail)
      return
    }

    setIsResending(true)
    try {
      await authApi.resendEmailVerification(normalizedEmail)
      setStatus('waiting')
      setMessage(t.verificationResent)
    } catch (error) {
      setStatus('error')
      setMessage(
        axios.isAxiosError(error) && error.response?.status === 429
          ? t.verificationTooMany
          : t.verificationUnable,
      )
    } finally {
      setIsResending(false)
    }
  }

  return (
    <GuestAuthShell>
      <Card className="relative w-full max-w-md border-white/60 bg-white/90 shadow-2xl backdrop-blur-sm">
        <CardHeader className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <MailCheck className="h-6 w-6" />
          </div>
          <CardTitle>{status === 'verifying' ? t.verifyingTitle : t.checkEmailTitle}</CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {status !== 'verifying' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="verification-email">{t.email}</Label>
                <Input
                  id="verification-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={t.emailPlaceholder}
                />
              </div>
              <Button type="button" loading={isResending} onClick={resend}>
                {t.sendVerificationLink}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                {t.alreadyVerified}{' '}
                <Link className="font-medium text-primary hover:underline" to="/login">
                  {t.signInLink}
                </Link>
              </p>
            </>
          ) : (
            <p className="text-center text-sm text-muted-foreground">{t.verifyingHint}</p>
          )}
        </CardContent>
      </Card>
    </GuestAuthShell>
  )
}
